import { useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AcademyPopup from "@/components/AcademyPopup";
import Hero from "@/components/sections/Hero";
import ChooseSystem from "@/components/sections/ChooseSystem";
import HowItWorks from "@/components/sections/HowItWorks";
import VideoGallery from "@/components/sections/VideoGallery";
import Ecosystem from "@/components/sections/Ecosystem";
import Pro48Spotlight from "@/components/sections/Pro48Spotlight";
import InTheWild from "@/components/sections/InTheWild";
import AcademyTeaser from "@/components/sections/AcademyTeaser";
import Founder from "@/components/sections/Founder";
import FinalCTA from "@/components/sections/FinalCTA";

export default function HomePage() {
  useEffect(() => {
    document.title =
      "Pickle Wall™ — Train Anytime. Dominate Everywhere. | Portable Pickleball Training Wall";
    const setMeta = (name, content, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta(
      "description",
      "Pickle Wall™ is a portable pro pickleball net — premium materials, sets up against any wall in under 5 minutes. Train anytime, anywhere, without a partner."
    );
    setMeta(
      "keywords",
      "pickleball training aid, pickleball trainer, pickleball practice wall, pickleball rebound trainer, pickleball drills, pickleball training equipment, pickleball practice equipment, beach pickleball, travel pickleball, vacation pickleball, pickleball lifestyle, mobile pickleball"
    );
    setMeta("og:title", "Pickle Wall™ — Train Anytime. Dominate Everywhere.", "property");
    setMeta(
      "og:description",
      "A portable pickleball training wall designed to help you train anytime, without needing a partner.",
      "property"
    );
    setMeta("og:type", "website", "property");
    setMeta("og:image", "https://itspicklewall.com/og-image.png", "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:image", "https://itspicklewall.com/og-image.png");

    // JSON-LD structured data
    const existing = document.getElementById("pw-schema");
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "pw-schema";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Product",
      name: "Pickle Wall™",
      description:
        "A portable pro pickleball net. Premium materials, sets up against any wall in under 5 minutes. Train anywhere, with or without a partner.",
      brand: { "@type": "Brand", name: "Pickle Wall" },
      offers: {
        "@type": "Offer",
        availability: "https://schema.org/PreOrder",
        priceCurrency: "USD",
      },
    });
    document.head.appendChild(script);

    // Beach Mode gallery photo structured data
    const existingGallery = document.getElementById("pw-gallery-schema");
    if (existingGallery) existingGallery.remove();
    const gallerySchema = document.createElement("script");
    gallerySchema.type = "application/ld+json";
    gallerySchema.id = "pw-gallery-schema";
    gallerySchema.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ImageGallery",
      name: "Pickle Wall — Real Reps. Real Results. Gallery",
      image: [
        {
          "@type": "ImageObject",
          contentUrl: "https://itspicklewall.com/images/gallery/pickle-wall-beach-mode.webp",
          thumbnailUrl: "https://itspicklewall.com/images/gallery/pickle-wall-beach-mode-800.webp",
          name: "Beach Mode",
          caption:
            "Pickle Wall goes wherever life takes you. Whether you're at the beach, the park, your driveway, or your garage, a few rallies are all it takes to stay connected to the game. Train a little. Relax a lot.",
          description:
            "Pickle Wall isn't just for the garage. It's designed to travel with you. Whether you're spending the day at the beach, camping with family, tailgating before the game, or relaxing in your backyard, Pickle Wall makes it easy to enjoy a few rallies wherever you are. Every place becomes an opportunity to play, connect, and enjoy the sport.",
          representativeOfPage: true,
          keywords: [
            "Beach Pickleball",
            "Portable Pickleball Net",
            "Pickle Wall",
            "Anywhere Anytime",
            "Pickleball Lifestyle",
            "Outdoor Training",
            "Vacation Pickleball",
            "Travel Pickleball",
            "Pickleball Training",
            "Mobile Pickleball",
          ],
          genre: ["Lifestyle", "Anywhere. Anytime.", "Community"],
        },
      ],
    });
    document.head.appendChild(gallerySchema);
  }, []);

  return (
    <main id="main-content" className="bg-[#0a0a0a] text-white min-h-screen">
      <AcademyPopup />
      <Navbar />
      <Hero />
      <ChooseSystem />
      <HowItWorks />
      <VideoGallery />
      <Ecosystem />
      <Pro48Spotlight />
      <InTheWild />
      <AcademyTeaser />
      <Founder />
      <FinalCTA />
      <Footer />
    </main>
  );
}
